import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PencilIcon } from 'common-assets/assets/icons/pencil-outlined.svg';
import { Icon, Typography } from 'ui-components';
import { RETIREMENT_CALCULATOR_NAMESPACE } from '../../tools/RetirementCalculator.const';
import {
  SectionHeaderPropsModel,
  SummaryRowModel,
} from './RetirementCalculatorSummary.model';

import useStyles from './RetirementCalculatorSummary.style';

interface SummarySectionPropsModel extends SectionHeaderPropsModel {
  rows: SummaryRowModel[];
  emptyState?: ReactNode;
}

export const SectionHeader: React.FC<SectionHeaderPropsModel> = ({ title, step, onEdit }) => {
  const [t] = useTranslation(RETIREMENT_CALCULATOR_NAMESPACE, { useSuspense: false });
  const c = useStyles({});

  return (
    <div className={c.sectionHeader}>
      <Typography variant="body1" component="h2" fontWeight="bold">
        {title}
      </Typography>
      {step !== undefined && (
        <button
          type="button"
          className={c.editButton}
          onClick={() => onEdit?.(step)}
          aria-label={t('summary.edit', { section: title })}
          data-testid={`RetirementCalculatorSummary-edit-${step}`}
        >
          <Icon className={c.editIcon}>
            <PencilIcon />
          </Icon>
        </button>
      )}
    </div>
  );
};

export const RetirementCalculatorSummarySection: React.FC<SummarySectionPropsModel> = ({
  title,
  rows,
  step,
  emptyState,
  onEdit,
}) => {
  const c = useStyles({});

  return (
    <div className={c.section}>
      <SectionHeader title={title} step={step} onEdit={onEdit} />
      {rows.length ? (
        <dl className={c.rows}>
          {rows.map(row => (
            <div key={row.key} className={c.row}>
              <dt className={c.rowLabel}>
                <Typography variant="body1" component="span" textColor="greys.60">
                  {row.label}
                </Typography>
              </dt>
              <dd className={c.rowValue}>
                <Typography variant="body1" component="span" fontWeight="bold" textColor="greys.60">
                  {row.value}
                </Typography>
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <Typography variant="body1" component="p" textColor="greys.60" className={c.emptyState}>
          {emptyState}
        </Typography>
      )}
    </div>
  );
};
